import type { TextContent, ToolResultMessage } from "../types";

/**
 * Shared machinery behind Checklist.tsx and Form.tsx: parsing what a resolved interactive call's
 * `result` actually says, and posting a fresh answer to a still-pending one. Kept in a plain .ts
 * file (no JSX) so it runs under `node --test` directly, matching tableData.ts's own rationale.
 */

/**
 * What an interactive component's `result` says, once the transcript has one.
 *
 * `buildInteractiveExecute` (src/runtime/components.ts) always resolves to either the browser's
 * submitted body, JSON-stringified verbatim, or `{cancelled: true, reason}` on timeout/cancel —
 * never a thrown error. Parsing that back is what lets a renderer show the same "answered" vs.
 * "expired" distinction after a page refresh that it shows live, from `result` alone (see
 * `ComponentProps.result`'s own doc comment in index.tsx: its presence is the only "already
 * submitted" signal, and there must not be another one).
 */
export type InteractiveOutcome =
	| { kind: "value"; value: Record<string, unknown> }
	| { kind: "cancelled"; reason: string }
	| { kind: "unparseable" };

export function parseInteractiveResult(
	result: ToolResultMessage,
): InteractiveOutcome {
	const text = result.content.find(
		(c): c is TextContent => c.type === "text",
	)?.text;
	if (typeof text !== "string") return { kind: "unparseable" };
	let parsed: unknown;
	try {
		parsed = JSON.parse(text);
	} catch {
		return { kind: "unparseable" };
	}
	if (!isPlainObject(parsed)) return { kind: "unparseable" };
	if (parsed.cancelled === true) {
		return {
			kind: "cancelled",
			reason:
				typeof parsed.reason === "string" ? parsed.reason : "已取消",
		};
	}
	return { kind: "value", value: parsed };
}

/**
 * Checklist.tsx's own reading of a "value" outcome: `{selected: string[]}`. Pulled out as a pure
 * function (rather than left inline in the component) specifically so it is unit-testable —
 * Checklist's post-fetch render can't be exercised by the SSR verification technique used for
 * this task (its rows come from a `useEffect`-driven fetch, which never fires under
 * `renderToStaticMarkup`), so this is the one place that render's most safety-critical bit of
 * logic — "which rows does the read-only view actually check" — gets real test coverage.
 *
 * Returns `undefined` both when there is nothing to show yet (not a "value" outcome at all) and
 * when `selected` is not actually an array — the latter means this component's declared `result`
 * schema does not match what Checklist expects (a profile-authoring mismatch), not a legitimate
 * empty answer. Checklist.tsx renders both the same way as `unparseable`: read-only, with an
 * explicit "couldn't read this back" note, rather than silently showing "nothing selected", which
 * would look identical to a real empty answer.
 */
export function selectedIdsFromOutcome(
	outcome: InteractiveOutcome | undefined,
): string[] | undefined {
	if (outcome?.kind !== "value") return undefined;
	if (!Array.isArray(outcome.value.selected)) return undefined;
	return outcome.value.selected.map(String);
}

/**
 * POSTs an interactive component's answer to the suspended tool call
 * (`POST /api/sessions/:id/components/:callId`, src/server/routes/components.ts). A direct
 * `fetch` here rather than going through `api.ts`'s `request()` helper — matching
 * `useWorkspaceJson.ts` next door, component fetches are scoped to session-relative routes that
 * stay out of the app-shell's shared API surface.
 */
export type SubmitResult =
	| { ok: true }
	| { ok: false; status: number; message: string };

export async function submitInteractiveResult(
	sessionId: string,
	callId: string,
	body: unknown,
): Promise<SubmitResult> {
	let res: Response;
	try {
		res = await fetch(`/api/sessions/${sessionId}/components/${callId}`, {
			method: "POST",
			credentials: "include",
			headers: { "content-type": "application/json" },
			body: JSON.stringify(body),
		});
	} catch (err) {
		// fetch only rejects when the request never reached the server (offline, DNS, ...) — status
		// 0 marks that apart from a real answer, same convention as api.ts's ApiError.isOffline.
		return {
			ok: false,
			status: 0,
			message: `提交失败：${err instanceof Error ? err.message : String(err)}`,
		};
	}
	if (res.ok) return { ok: true };
	const parsedBody = await res.json().catch(() => undefined);
	const serverMessage =
		isPlainObject(parsedBody) && typeof parsedBody.error === "string"
			? parsedBody.error
			: undefined;
	return {
		ok: false,
		status: res.status,
		message: serverMessage ?? fallbackMessage(res.status),
	};
}

/**
 * 409 (the call is gone — answered elsewhere, timed out, or cancelled) and 403 (not this
 * session): resubmitting can never succeed for either, unlike every other failure (400 — the body
 * didn't match the schema, but the call itself is untouched; a network blip; anything
 * unexpected), where the pending call is presumably still open and worth letting the user retry.
 * Exported separately from the fetch above so this distinction is testable without mocking
 * `fetch`.
 */
export function isGoneStatus(status: number): boolean {
	// 404 means the session record itself is gone, so the pending call it belonged to can never
	// be resolved. Leaving submit enabled there invites a retry that can only fail again.
	// 423 is deliberately not here: it means the other person is driving right now, the call is
	// still open, and the same submission works once they are done.
	return status === 409 || status === 403 || status === 404;
}

/**
 * True when this call can never be answered again, whichever way it died.
 *
 * Three things kill a pending call, and none of them is the reader's mistake: they typed a reply
 * instead of using the component (`sessions.ts` cancels every pending call on a new message), the
 * 30-minute timeout elapsed, or the server restarted and the in-memory waiter went with it. The
 * first arrives as a `cancelled` outcome on the transcript, the last two as a gone status on the
 * submit. Both render identically — the choice stays on screen, disabled — so both answer to one
 * predicate rather than two branches that could drift apart.
 */
export function isGone(
	outcome: InteractiveOutcome | undefined,
	failedStatus: number | undefined,
): boolean {
	if (outcome?.kind === "cancelled") return true;
	return failedStatus !== undefined && isGoneStatus(failedStatus);
}

/**
 * What to say when a call is gone. Deliberately not an error, and deliberately not the server's
 * own `reason` ("user replied directly, without using the component"), which is written for
 * whoever debugs this rather than for whoever is looking at it: the only useful content is that
 * the click will not land and what to do instead.
 */
export const GONE_NOTICE = {
	checklist: "已作出选择",
	form: "已提交",
} as const;

function fallbackMessage(status: number): string {
	if (status === 409) return "操作已过期";
	if (status === 423) return "对话被占用，请稍后提交";
	if (status === 403) return "无权提交";
	if (status === 404) return "对话已失效，无法提交";
	if (status === 400)
		return "提交内容格式不符，请检查后重试";
	return `提交失败（HTTP ${status}）`;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null && !Array.isArray(value);
}
